import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { usePatientPrivacy } from "hooks/use-patient-privacy.hook";
import { useGetPatientQuery } from "services/patients";
import type { Patient } from "services/types/patients.type";
import { getFullName } from "utils/common";

/** What the launcher believes the user is looking at. */
export type QuickLauncherContextType = "patient" | "sale";

export interface QuickLauncherContext {
  type: QuickLauncherContextType;
  patientId: number;
  /** Present only for `type === "sale"`. The sale id doubles as the invoice number. */
  saleId?: number;
  /**
   * A narrow projection of the patient — enough for the touchpoint, email and
   * dial surfaces to address them. `undefined` while the fetch is in flight
   * (the id-only actions are offered meanwhile).
   */
  patient?: Patient;
  /** Privacy-aware display name; falls back to the id until the patient loads. */
  patientLabel: string;
  /** True on `/patients/:id/billing/:saleId/invoice` — the one sale action that is redundant there. */
  isOnInvoicePage: boolean;
}

/**
 * Sub-tabs of the chart all live under this. `/patients/new` and `/patients`
 * do not match a numeric id and so yield no context.
 */
export const PATIENT_ROUTE_PATTERN = "/patients/:patientId/*";
/**
 * Every sale surface — invoice, edit, payment, adjustment, claim, refund — is
 * a child of this. `billing/new` and `billing/payment-requests/:id` match the
 * pattern too, which is why `saleId` is checked for being an integer below.
 */
export const SALE_ROUTE_PATTERN = "/patients/:patientId/billing/:saleId/*";

/**
 * Deliberately not `PATIENT_HEADER_QUERY` (~4 KB, dozens of nested objects):
 * RTK Query keys the cache on the full argument, so the chart's own fetch
 * cannot be reused anyway, and this list is exactly what the contextual
 * actions read — name for the label, contact fields for text/email/call,
 * `preferred_clinic` because `NewTouchpointForm` reads it.
 */
export const QUICK_LAUNCHER_CONTEXT_PATIENT_QUERY =
  "{id,first_name,middle_name,preferred_name,last_name,title,suffix,phone,phone_type,secondary_phone,email,secondary_email,is_active,do_not_call,do_not_email,do_not_message,do_not_contact,preferred_clinic{id,name,display_name,timezone}}";

const parseRouteId = (raw: string | undefined): number | null => {
  if (raw === undefined || !/^\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

/** Mirrors `SetPatientDocumentTitle`: initials only while privacy mode is on. */
export const formatContextPatientLabel = (
  patient: Patient | undefined,
  patientId: number,
  isPrivateMode: boolean
): string => {
  if (!patient) return `Patient #${patientId}`;
  if (isPrivateMode) {
    const initials = [
      patient.first_name?.charAt(0),
      patient.last_name?.charAt(0)
    ]
      .filter(Boolean)
      .map((char) => `${String(char).toUpperCase()}.`)
      .join(" ");
    return initials || `Patient #${patientId}`;
  }
  return (
    patient.fullName ||
    getFullName({
      firstName: patient.first_name || "",
      middleName: patient.middle_name,
      preferredName: patient.preferred_name,
      lastName: patient.last_name || ""
    }) ||
    `Patient #${patientId}`
  );
};

/**
 * Where the user is, as far as the launcher cares: a patient chart, or a sale
 * inside one. Read from the URL rather than from `PatientProvider`, because the
 * launcher is mounted in `Layout` above the router outlet, where
 * `usePatientOptional()` is always `null`.
 *
 * `enabled` is the feature flag — when it is off nothing is fetched, so the
 * shell makes no extra request for users who will never see the panel.
 */
export default function useQuickLauncherContext(
  enabled: boolean
): QuickLauncherContext | null {
  const { pathname } = useLocation();
  const { isPrivateMode } = usePatientPrivacy();

  const ids = useMemo(() => {
    const saleMatch = matchPath(SALE_ROUTE_PATTERN, pathname);
    const patientMatch =
      saleMatch ?? matchPath(PATIENT_ROUTE_PATTERN, pathname);
    const patientId = parseRouteId(patientMatch?.params.patientId);
    if (patientId === null) return null;
    const saleId = parseRouteId(saleMatch?.params.saleId);
    return {
      patientId,
      saleId,
      isOnInvoicePage:
        saleId !== null &&
        pathname.replace(/\/+$/, "").endsWith(`/billing/${saleId}/invoice`)
    };
  }, [pathname]);

  const { data: patient } = useGetPatientQuery(
    {
      patientId: ids?.patientId ?? 0,
      query: QUICK_LAUNCHER_CONTEXT_PATIENT_QUERY
    },
    { skip: !enabled || !ids }
  );

  return useMemo(() => {
    if (!enabled || !ids) return null;
    // `skip` does not clear `data`: navigating from one chart to another can
    // briefly leave the previous patient in `patient`. Gate on the id so the
    // label never names the wrong person.
    const loadedPatient = patient?.id === ids.patientId ? patient : undefined;
    return {
      type: ids.saleId !== null ? "sale" : "patient",
      patientId: ids.patientId,
      saleId: ids.saleId ?? undefined,
      patient: loadedPatient,
      patientLabel: formatContextPatientLabel(
        loadedPatient,
        ids.patientId,
        isPrivateMode
      ),
      isOnInvoicePage: ids.isOnInvoicePage
    };
  }, [enabled, ids, patient, isPrivateMode]);
}
